import { useCallback, useEffect, useState } from "react";
import api, { inr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useRealtime } from "@/lib/realtime";
import DashboardLayout from "@/components/DashboardLayout";
import DashboardExtras from "@/components/DashboardExtras";
import RankProgressCard from "@/components/RankProgressCard";
import SmartDashboard from "@/pages/dist/SmartDashboard";
import { Link } from "react-router-dom";
import {
  User, Network, Users, TrendingUp, MessageSquare, Copy, Trophy,
  Calendar, Phone, Award, CheckCircle2, Clock, XCircle,
} from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

function StatusPill({ value }) {
  const v = (value || "").toLowerCase();
  const map = {
    approved: { cls: "bg-olive/10 text-olive border-olive/20", icon: CheckCircle2 },
    submitted: { cls: "bg-gold/10 text-gold-deep border-gold/20", icon: Clock },
    pending: { cls: "bg-navy/5 text-navy/60 border-navy/15", icon: Clock },
    rejected: { cls: "bg-rose/10 text-rose border-rose/20", icon: XCircle },
  };
  const m = map[v] || map.pending;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider ${m.cls}`}>
      <Icon size={10} /> {value || "Pending"}
    </span>
  );
}

const QUICK_LINKS = [
  { to: "/kyc", label: "Profile", icon: User, tone: "olive" },
  { to: "/dist/downline", label: "My Directs", icon: Users, tone: "rose" },
  { to: "/dist/tree", label: "Team View", icon: Network, tone: "gold" },
  { to: "/dist/wallet", label: "Incentive", icon: TrendingUp, tone: "navy" },
  { to: "/contact", label: "Complaint", icon: MessageSquare, tone: "rose-deep" },
];

export default function DashboardPro() {
  // The default distributor dashboard now renders the SmartValue-inspired
  // three-column layout. `OverviewPanel` below is kept for admin usage.
  return <SmartDashboard />;
}

/** Reusable overview panel — used by both DashboardPro (self) and admin MemberDetail. */
export function OverviewPanel({ data: d, rewards }) {
  const refLink = `${window.location.origin}${d.referral_link}`;
  const copy = () => { navigator.clipboard.writeText(refLink); toast.success("Referral link copied"); };
  const team1 = d.team_stats.team1;
  const team2 = d.team_stats.team2;
  const tot = d.team_stats.total;
  const vol = d.generation.volume;

  return (
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5" data-testid="dashboard-pro">
        {/* ============ LEFT COLUMN ============ */}
        <div className="lg:col-span-3 space-y-5">
          {/* Profile card */}
          <div className="bg-white border border-navy/15 rounded-lg overflow-hidden shadow-sm" data-testid="profile-card">
            <div className="bg-gradient-to-br from-cream/80 to-cream/40 px-5 pt-6 pb-4 text-center border-b border-navy/10">
              <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-gold/40 to-cream grid place-items-center border-2 border-gold/40 shadow-inner">
                <User size={42} className="text-navy/70" />
              </div>
              <div className="font-serif text-xl font-extrabold text-navy mt-3">{d.profile.name}</div>
              <div className="text-xs uppercase tracking-wider text-navy/70 font-bold mt-1">{d.profile.user_code}</div>
              {d.profile.active ? (
                <span className="inline-block mt-2.5 text-xs uppercase tracking-wider font-bold px-2.5 py-1 bg-olive/10 text-olive border border-olive/25 rounded-full">Active · {d.profile.package_code || ""}</span>
              ) : (
                <span className="inline-block mt-2.5 text-xs uppercase tracking-wider font-bold px-2.5 py-1 bg-rose/10 text-rose border border-rose/25 rounded-full">Inactive</span>
              )}
            </div>
            <div className="grid grid-cols-3 text-center text-xs">
              <div className="p-3 border-r border-navy/10">
                <div className="text-xs uppercase tracking-wider text-navy/70 font-bold inline-flex items-center gap-1"><Phone size={10} /> Mobile</div>
                <div className="text-navy font-semibold mt-1 truncate">{d.profile.mobile || "—"}</div>
              </div>
              <div className="p-3 border-r border-navy/10">
                <div className="text-xs uppercase tracking-wider text-navy/70 font-bold inline-flex items-center gap-1"><Calendar size={10} /> DOJ</div>
                <div className="text-navy font-semibold mt-1">{fmtDate(d.profile.doj)}</div>
              </div>
              <div className="p-3">
                <div className="text-xs uppercase tracking-wider text-navy/70 font-bold inline-flex items-center gap-1"><Award size={10} /> DOA</div>
                <div className="text-navy font-semibold mt-1">{fmtDate(d.profile.doa)}</div>
              </div>
            </div>
          </div>

          {/* Income card */}
          <div className="bg-white border border-navy/15 rounded-lg shadow-sm" data-testid="income-card">
            <div className="px-5 py-3 border-b-2 border-gold/40 bg-cream/40">
              <div className="font-serif text-lg font-bold text-navy">My Income</div>
            </div>
            <div className="p-4 space-y-2">
              <IncomeRow label="Matching Income" value={d.income.matching} />
              <IncomeRow label="Startup Income" value={d.income.startup || 0} />
              <IncomeRow label="Booster" value={d.income.booster || 0} />
              <IncomeRow label="Self Retail" value={d.income.self_retail || 0} />
              <IncomeRow label="Reward Wallet" value={d.income.reward} />
              <div className="bg-gradient-to-r from-olive to-olive/90 text-white rounded-md px-4 py-3 mt-3 flex items-center justify-between shadow-sm" data-testid="total-income">
                <span className="text-xs uppercase tracking-wider font-bold">Total Income</span>
                <span className="font-serif text-xl font-extrabold tabular-nums lining-nums">{inr(d.income.total)}</span>
              </div>
            </div>
          </div>

          {/* Rank Progress + Boons Ladder (new pair-based plan) */}
          <RankProgressCard rewards={rewards} />

          {/* Reward card */}
          <div className="bg-white border border-navy/15 rounded-lg shadow-sm" data-testid="reward-card">
            <div className="px-5 py-3 border-b-2 border-gold/40 flex items-center gap-2 bg-cream/40">
              <Trophy size={16} className="text-gold-deep" />
              <div className="font-serif text-lg font-bold text-navy">Reward</div>
            </div>
            <div className="p-4 grid grid-cols-3 gap-2 text-xs">
              <div>
                <div className="text-xs uppercase tracking-wider text-navy/70 font-bold mb-1">Time Limit</div>
                <div className="text-navy font-extrabold text-base">{d.reward.time_limit.value}</div>
                <div className="text-xs text-navy/60 font-medium mt-0.5">{d.reward.time_limit.title}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-navy/70 font-bold mb-1">No Limit</div>
                <div className="text-navy font-extrabold text-base">{d.reward.no_time_limit.value}</div>
                <div className="text-xs text-navy/60 font-medium mt-0.5">{d.reward.no_time_limit.title}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-navy/70 font-bold mb-1">Remaining</div>
                <div className="text-navy font-extrabold text-base">{d.reward.remaining_days} days</div>
              </div>
            </div>
          </div>

          {/* KYC card */}
          <div className="bg-white border border-navy/15 rounded-lg shadow-sm" data-testid="kyc-card">
            <div className="px-5 py-3 border-b-2 border-gold/40 bg-cream/40">
              <div className="font-serif text-lg font-bold text-navy">KYC</div>
            </div>
            <div className="p-2">
              {d.kyc_docs.length === 0 ? (
                <div className="text-center py-4 text-sm text-navy/70 font-medium">
                  No documents uploaded yet.
                  <Link to="/kyc" className="block mt-2 text-gold-deep font-bold underline">Upload now →</Link>
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-navy/70 border-b border-navy/10 bg-cream/30">
                      <th className="text-left px-2 py-2 font-bold uppercase tracking-wider text-xs">Type</th>
                      <th className="text-left px-2 py-2 font-bold uppercase tracking-wider text-xs">Status</th>
                      <th className="text-left px-2 py-2 font-bold uppercase tracking-wider text-xs">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.kyc_docs.slice(0, 6).map((k, i) => (
                      <tr key={i} className="border-b border-navy/5 last:border-0">
                        <td className="px-2 py-2 text-navy font-semibold">{k.type}</td>
                        <td className="px-2 py-2"><StatusPill value={k.decision || k.status} /></td>
                        <td className="px-2 py-2 text-navy/70 font-medium">{k.date || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* ============ CENTER COLUMN ============ */}
        <div className="lg:col-span-6 space-y-5">
          {/* Quick Links */}
          <div className="bg-white border border-navy/15 rounded-lg shadow-sm" data-testid="quick-links">
            <div className="px-5 py-3 border-b-2 border-rose/40 bg-cream/40">
              <div className="font-serif text-lg font-bold text-navy">Dashboard Quick Links</div>
            </div>
            <div className="px-5 py-6 grid grid-cols-5 gap-3">
              {QUICK_LINKS.map((q) => {
                const Icon = q.icon;
                return (
                  <Link
                    key={q.label}
                    to={q.to}
                    data-testid={`quick-${q.label.toLowerCase().replace(/ /g, "-")}`}
                    className="group flex flex-col items-center gap-2 p-3 rounded-md border border-navy/15 hover:border-gold/50 hover:bg-cream/50 hover:shadow-sm transition-all"
                  >
                    <div className={`w-12 h-12 rounded-full grid place-items-center border-2 ${
                      q.tone === "gold" ? "bg-gold/10 border-gold/40 text-gold-deep" :
                      q.tone === "olive" ? "bg-olive/10 border-olive/40 text-olive" :
                      q.tone === "rose" ? "bg-rose/10 border-rose/40 text-rose" :
                      q.tone === "rose-deep" ? "bg-rose/15 border-rose/50 text-rose" :
                      "bg-navy/5 border-navy/25 text-navy"
                    }`}>
                      <Icon size={22} />
                    </div>
                    <span className="text-xs uppercase tracking-wider text-navy/80 font-bold group-hover:text-navy">{q.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Referral Link */}
          <div className="bg-white border border-navy/15 rounded-lg shadow-sm" data-testid="referral-link-card">
            <div className="px-5 py-3 border-b border-navy/10 bg-cream/40">
              <div className="font-serif text-lg font-bold text-navy">My Referral Link</div>
            </div>
            <div className="p-4 flex flex-col sm:flex-row items-stretch gap-2">
              <div className="flex-1 bg-cream/60 border border-navy/15 rounded-md px-3 py-3 text-sm text-navy break-all font-mono font-semibold" data-testid="referral-link-text">
                {refLink}
              </div>
              <button
                onClick={copy}
                data-testid="referral-copy-btn"
                className="bg-olive text-white px-6 py-3 text-xs font-bold uppercase tracking-wider hover:bg-olive/90 transition-colors inline-flex items-center gap-2 rounded-md shadow-sm"
              >
                <Copy size={14} /> Copy
              </button>
            </div>
          </div>

          {/* My Team Details */}
          <div className="bg-white border border-navy/15 rounded-lg shadow-sm" data-testid="team-details">
            <div className="px-5 py-3 border-b border-navy/10 flex items-center justify-between gap-3 bg-cream/40">
              <div className="font-serif text-lg font-bold text-navy">My Team Details</div>
              <ActivitySparkline data={d.weekly_activity || []} />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-navy/15 bg-cream/50">
                    <th className="text-left px-4 py-3 font-bold text-xs uppercase tracking-wider text-navy/80"></th>
                    <th className="text-right px-4 py-3 font-bold text-xs uppercase tracking-wider text-navy/80">Team 1</th>
                    <th className="text-right px-4 py-3 font-bold text-xs uppercase tracking-wider text-navy/80">Team 2</th>
                    <th className="text-right px-4 py-3 font-bold text-xs uppercase tracking-wider text-navy/80">Total</th>
                  </tr>
                </thead>
                <tbody className="text-navy">
                  <TeamRow label="Today's Registration" k="today_reg" t1={team1} t2={team2} tot={tot} />
                  <TeamRow label="Today's Activation" k="today_act" t1={team1} t2={team2} tot={tot} />
                  <TeamRow label="Total Registration" k="total_reg" t1={team1} t2={team2} tot={tot} />
                  <TeamRow label="Total Activation" k="total_act" t1={team1} t2={team2} tot={tot} />
                  <TeamRow label="Total Direct Active" k="total_direct_active" t1={team1} t2={team2} tot={tot} />
                  <TeamRow label="Total FV" k="total_fv" t1={team1} t2={team2} tot={tot} />
                  <TeamRow label="Total Direct FV" k="total_direct_fv" t1={team1} t2={team2} tot={tot} />
                  <TeamRow label="Total Reward Point" k="total_reward_point" t1={team1} t2={team2} tot={tot} />
                </tbody>
              </table>
            </div>
            <div className="px-5 py-2.5 text-xs text-navy/70 italic border-t border-navy/10 font-medium">
              * For more detail please click on <Link to="/tree" className="text-gold-deep font-bold not-italic underline">[View]</Link> link.
            </div>
          </div>

          {/* Generation Details */}
          <div className="bg-white border border-navy/15 rounded-lg shadow-sm" data-testid="generation-details">
            <div className="px-5 py-3 border-b border-navy/10 bg-cream/40">
              <div className="font-serif text-lg font-bold text-navy">My Generation Details</div>
            </div>
            <div className="overflow-x-auto p-4 pt-2">
              <table className="w-full text-sm mb-4">
                <thead>
                  <tr className="border-b-2 border-navy/15 bg-cream/50">
                    <th className="text-left px-4 py-2.5 font-bold text-xs uppercase tracking-wider text-navy/80"></th>
                    <th className="text-right px-4 py-2.5 font-bold text-xs uppercase tracking-wider text-navy/80">Direct</th>
                    <th className="text-right px-4 py-2.5 font-bold text-xs uppercase tracking-wider text-navy/80">Indirect</th>
                    <th className="text-right px-4 py-2.5 font-bold text-xs uppercase tracking-wider text-navy/80">Total</th>
                  </tr>
                </thead>
                <tbody className="text-navy">
                  <tr className="border-b border-navy/5">
                    <td className="px-4 py-2.5 font-semibold">PC Count</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium">{d.generation.pc_count.direct}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium">{d.generation.pc_count.indirect}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-extrabold">{d.generation.pc_count.total}</td>
                  </tr>
                </tbody>
              </table>

              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-navy/15 bg-cream/50">
                    <th className="text-left px-4 py-2.5 font-bold text-xs uppercase tracking-wider text-navy/80"></th>
                    <th className="text-right px-4 py-2.5 font-bold text-xs uppercase tracking-wider text-navy/80">Till Last Month</th>
                    <th className="text-right px-4 py-2.5 font-bold text-xs uppercase tracking-wider text-navy/80">Current Month</th>
                    <th className="text-right px-4 py-2.5 font-bold text-xs uppercase tracking-wider text-navy/80">Accumulated</th>
                  </tr>
                </thead>
                <tbody className="text-navy">
                  <VolRow label="Self PV" t={vol} k="self_pv"  tip="Points from your OWN personal purchases (packages + orders you buy for yourself). Drives rank + matching income." />
                  <VolRow label="Self RV" t={vol} k="self_rv" rupee tip="Retail ₹ revenue from your OWN retail sales (products you sold directly to end-customers, not through downline)." />
                  <VolRow label="Group PV" t={vol} k="group_pv" tip="Combined PV of your ENTIRE downline (both legs, all levels deep). Excludes your own Self PV." />
                  <VolRow label="Group RV" t={vol} k="group_rv" rupee tip="Combined retail-sale ₹ revenue from your entire downline." />
                  <VolRow label="Total PV" t={vol} k="total_pv" bold tip="Self PV + Group PV. Grand total point score used for rank qualification and matching bonus." />
                  <VolRow label="Total RV" t={vol} k="total_rv" rupee bold tip="Self RV + Group RV. Grand total retail revenue you and your team pushed, used for retail bonus tiers." />
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* ============ RIGHT COLUMN ============ */}
        <div className="lg:col-span-3 space-y-5">
          {/* Information */}
          <div className="bg-white border border-navy/15 rounded-lg shadow-sm min-h-[200px]" data-testid="information-card">
            <div className="px-5 py-3 border-b-2 border-rose/40 bg-cream/40">
              <div className="font-serif text-lg font-bold text-rose">Information</div>
            </div>
            <div className="p-4">
              {d.notices && d.notices.length > 0 ? (
                <ul className="space-y-3 text-sm text-navy">
                  {d.notices.map((n, i) => (
                    <li key={i} className="border-l-4 border-gold/60 pl-3">
                      <div className="font-bold text-navy">{n.title}</div>
                      <div className="text-navy/70 font-medium text-xs mt-0.5">{n.body}</div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-navy/60 italic text-center py-8 font-medium">
                  No new notices.
                  <br /> Stay tuned for updates from SAMARA.
                </div>
              )}
            </div>
          </div>

          {/* My Active Directs */}
          <div className="bg-white border border-navy/15 rounded-lg shadow-sm" data-testid="active-directs">
            <div className="px-5 py-3 border-b border-navy/10 bg-cream/40">
              <div className="font-serif text-lg font-bold text-navy">My Active Directs</div>
            </div>
            {d.active_directs.length === 0 ? (
              <div className="p-6 text-center text-sm text-navy/70 font-medium">
                No active directs yet.
                <br />Share your referral link to start.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b-2 border-navy/15 bg-cream/50 text-navy/80">
                      <th className="text-left px-3 py-2.5 font-bold uppercase tracking-wider text-xs">S.No</th>
                      <th className="text-left px-3 py-2.5 font-bold uppercase tracking-wider text-xs">ID No</th>
                      <th className="text-left px-3 py-2.5 font-bold uppercase tracking-wider text-xs">Member</th>
                      <th className="text-left px-3 py-2.5 font-bold uppercase tracking-wider text-xs">Position</th>
                    </tr>
                  </thead>
                  <tbody className="text-navy">
                    {d.active_directs.slice(0, 11).map((u, i) => (
                      <tr key={u.user_code} className="border-b border-navy/5 last:border-0 hover:bg-cream/30">
                        <td className="px-3 py-2 text-navy/70 font-semibold">{i + 1}.</td>
                        <td className="px-3 py-2 font-mono text-xs font-bold">{u.user_code}</td>
                        <td className="px-3 py-2 truncate max-w-[100px] font-semibold" title={u.name}>{u.name?.toUpperCase()}</td>
                        <td className="px-3 py-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full border font-bold ${
                            u.position === "TEAM1" ? "bg-olive/10 text-olive border-olive/30" : "bg-gold/10 text-gold-deep border-gold/30"
                          }`}>{u.position}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="px-5 py-2.5 border-t border-navy/10 text-right">
              <Link to="/downline" className="text-xs text-gold-deep font-bold underline">View All Directs →</Link>
            </div>
          </div>
        </div>
      </div>
  );
}

function IncomeRow({ label, value }) {
  return (
    <div className="flex items-center justify-between border border-navy/15 rounded-md px-3.5 py-2.5 bg-cream/30 hover:bg-cream/50 transition-colors">
      <span className="text-xs uppercase tracking-wider text-navy/80 font-bold">{label}</span>
      <span className="font-mono text-base font-extrabold text-navy tabular-nums">{inr(value)}</span>
    </div>
  );
}

function TeamRow({ label, k, t1, t2, tot }) {
  return (
    <tr className="border-b border-navy/5 last:border-0 hover:bg-cream/30">
      <td className="px-4 py-2.5 text-navy font-semibold">{label}</td>
      <td className="px-4 py-2.5 text-right tabular-nums text-navy/80 font-medium">{(t1[k] ?? 0).toLocaleString("en-IN")}</td>
      <td className="px-4 py-2.5 text-right tabular-nums text-navy/80 font-medium">{(t2[k] ?? 0).toLocaleString("en-IN")}</td>
      <td className="px-4 py-2.5 text-right tabular-nums font-extrabold text-navy">{(tot[k] ?? 0).toLocaleString("en-IN")}</td>
    </tr>
  );
}

function VolRow({ label, t, k, rupee = false, bold = false, tip = "" }) {
  const F = (n) => rupee ? `₹${fmt(n)}` : (n || 0).toLocaleString("en-IN");
  const cls = bold ? "font-extrabold" : "font-medium";
  return (
    <tr className="border-b border-navy/5 last:border-0 hover:bg-cream/30">
      <td className={`px-4 py-2.5 text-navy ${bold ? "font-extrabold" : "font-semibold"}`}>
        <span className="inline-flex items-center gap-1.5">
          {label}
          {tip ? (
            <span
              tabIndex={0}
              aria-label={tip}
              title={tip}
              data-testid={`vol-tip-${k}`}
              className="relative group inline-flex items-center justify-center w-4 h-4 rounded-full border border-navy/30 text-navy/70 hover:text-royal hover:border-royal text-[10px] leading-none cursor-help outline-none focus:ring-2 focus:ring-royal/30 font-bold"
            >
              i
              <span
                role="tooltip"
                className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-30 w-64 max-w-[80vw] rounded-md bg-navy text-white text-xs leading-snug px-3 py-2 shadow-xl opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity font-medium"
              >
                {tip}
              </span>
            </span>
          ) : null}
        </span>
      </td>
      <td className={`px-4 py-2.5 text-right tabular-nums ${cls}`}>{F(t.till_last_month[k])}</td>
      <td className={`px-4 py-2.5 text-right tabular-nums ${cls}`}>{F(t.current_month[k])}</td>
      <td className={`px-4 py-2.5 text-right tabular-nums ${cls}`}>{F(t.accumulated[k])}</td>
    </tr>
  );
}

/** 7-day registration + activation sparkline. Compact, inline-SVG. */
function ActivitySparkline({ data }) {
  if (!data || data.length === 0) return null;
  const w = 160, h = 36, pad = 2;
  const totals = data.map((d) => d.total || 0);
  const max = Math.max(1, ...totals);
  const stepX = (w - pad * 2) / Math.max(1, data.length - 1);
  const points = totals.map((v, i) => {
    const x = pad + i * stepX;
    const y = h - pad - (v / max) * (h - pad * 2);
    return `${x},${y}`;
  }).join(" ");
  const last = data[data.length - 1] || {};
  return (
    <div data-testid="activity-sparkline" className="flex items-center gap-2" title={`Last 7 days · today: ${last.registrations || 0} reg, ${last.activations || 0} act`}>
      <svg width={w} height={h} className="overflow-visible">
        <polyline
          fill="none"
          stroke="#C9A961"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={points}
        />
        {totals.map((v, i) => {
          const x = pad + i * stepX;
          const y = h - pad - (v / max) * (h - pad * 2);
          return <circle key={i} cx={x} cy={y} r={v > 0 ? 2 : 1} fill={v > 0 ? "#6B8E23" : "#1F2A4E33"} />;
        })}
      </svg>
      <div className="text-[10px] uppercase tracking-widest text-navy/50">
        Today<br />
        <span className="text-olive font-medium">{last.total || 0}</span>
      </div>
    </div>
  );
}
